/* eslint-disable class-methods-use-this */
import { ComputedRef, computed, ref } from 'vue';

import { fetchDividasData, syncAllDividas, temCacheSyncAllValido } from '@/composables/useDividasQueries';
import { dividaPrimeOrdenacaoService } from '@/features/prime/services/ordenacao/DividaPrimeOrdenacaoService';
import { calcularChaveDivida } from '@/helpers/dividaKey';
import { getDividasThresholdTempoMs } from '@/helpers/dividasThreshold';
import DividaDto from '@/models/dto/DividaDto';
import { DividaResource } from '@/models/resources';
import SincronizacaoResource from '@/models/resources/SincronizacaoResource';
import { dividaService, propostaService, sessionService } from '@/services';
import useDeeplink from '@/stores/deeplink';
import ConvivenciaWlSevice from './ConvivenciaWlSevice';
import { getDividasOrderingService } from './DividasOrderingService';
import ordenacaoBackendService, { OrdenacaoBackendResponse } from './OrdenacaoBackendService';
import { OrdenacaoPorModelagemAcertoStrategy } from './ordering/strategies/OrdenacaoPorModelagemAcertoStrategy';
import { TipoOrdenacao } from './ordering/types/TipoOrdenacao';
import syncDuracaoDividaTracker from './SyncDuracaoDividaTracker';

/**
 * Interface para resultado da listagem de todas as dívidas
 */
export interface ListarDividasOutput {
  todasDividas: DividaResource[];
  dividasDisponiveisNegociacao: DividaResource[];
  dividasDisponiveisNegociacaoOrdenadas: DividaResource[];
}

/**
 * ViewModel reativo para as telas se plugarem
 */
export interface DividasSyncViewModel {
  todasDividas: DividaResource[];
  dividasDisponiveisNegociacao: DividaResource[];
  dividasDisponiveisNegociacaoOrdenadas: DividaResource[];
  dividasPrimeiroLote: DividaResource[];
  dividasSegundoLote: DividaResource[];
  infoCredor: Record<string, SincronizacaoResource['infoCredor']>;
  progressoSincronizacao: number;
  isLoading: boolean;
  todasChamadasPossuemErro: boolean;
  thresholdPassou: boolean;
  veioDoCache: boolean;
  exibirTodasDividas: boolean;
}

interface DividasPorLote {
  dividasPrimeiroLote: DividaResource[];
  dividasSegundoLote: DividaResource[];
}

/**
 * Serviço intermediário para sincronização e ordenação de dívidas
 * Inspirado no PropostaService, mas chama DividaService para requisições HTTP
 */
class DividasSyncService {
  private static readonly PROGRESSO_INICIAL = 0;
  private static readonly PROGRESSO_MAXIMO_LOADING = 90;
  private static readonly PROGRESSO_POR_RETORNO = 5;

  private todasDividas = ref<DividaResource[]>([]);
  private readonly infoCredor = ref<Record<string, SincronizacaoResource['infoCredor']>>({});
  private readonly progressoSincronizacao = ref<number>(DividasSyncService.PROGRESSO_INICIAL);
  private readonly credoresComProgresso = ref<Set<string>>(new Set());
  private isLoading = ref(false);
  private readonly todasChamadasPossuemErro = ref(false);
  private thresholdPassou = ref(false);
  private veioDoCache = ref(false);
  private readonly exibirTodasDividas = ref(false);
  private primeiroLote: DividaResource[] = [];
  private chavesPrimeiroLote = ref<Set<string>>(new Set());
  private ordenacaoRecomendadaPorModelo: OrdenacaoBackendResponse | null = null;
  private readonly ordenacaoVersao = ref(0);

  public viewModel: ComputedRef<DividasSyncViewModel>;

  /**
   * Gera chave única para identificar uma dívida (api-id).
   */
  private static calcularKey(api: string, id: number): string {
    return calcularChaveDivida(api, id);
  }

  private zerarEstado(): void {
    this.todasChamadasPossuemErro.value = false;
    this.thresholdPassou.value = false;
    this.veioDoCache.value = false;
    this.exibirTodasDividas.value = false;
    this.todasDividas.value = [];
    this.infoCredor.value = {};
    this.progressoSincronizacao.value = DividasSyncService.PROGRESSO_INICIAL;
    this.credoresComProgresso.value = new Set();
    this.primeiroLote = [];
    this.chavesPrimeiroLote.value = new Set();
    dividaPrimeOrdenacaoService.reset();

    this.ordenacaoRecomendadaPorModelo = null;
    this.ordenacaoVersao.value = 0;
    syncDuracaoDividaTracker.reset();
  }

  private incrementarProgressoPorCredor(api?: string): void {
    if (!api || this.credoresComProgresso.value.has(api)) {
      return;
    }

    this.credoresComProgresso.value.add(api);

    if (!this.isLoading.value) {
      return;
    }

    const progressoAtual = this.progressoSincronizacao.value;
    const progressoMaximo = DividasSyncService.PROGRESSO_MAXIMO_LOADING;
    if (progressoAtual >= progressoMaximo) {
      return;
    }

    const incremento = DividasSyncService.PROGRESSO_POR_RETORNO;

    this.progressoSincronizacao.value = Math.min(progressoMaximo, progressoAtual + incremento);
  }

  /** Logout / troca de usuário: zera dados sem marcar sync em andamento. */
  public limparEstadoSessao(): void {
    this.zerarEstado();
    this.isLoading.value = false;
  }

  private obterOutputAtual(): ListarDividasOutput {
    return {
      todasDividas: this.viewModel.value.todasDividas,
      dividasDisponiveisNegociacao: this.viewModel.value.dividasDisponiveisNegociacao,
      dividasDisponiveisNegociacaoOrdenadas: this.viewModel.value.dividasDisponiveisNegociacaoOrdenadas,
    };
  }

  /**
   * Define se todas as dívidas devem ser exibidas
   */
  public setExibirTodasDividas(value: boolean): void {
    this.exibirTodasDividas.value = value;
  }

  /**
   * Seleciona a dívida do deeplink (`credorPrioritarioDeeplink` + `dividaIdPrioritarioDeeplink`),
   * se existir em `dividasBase`. Regra de prioridade na listagem, não de domínio de nenhuma feature.
   */
  private static selecionarDeeplink(
    dividasBase: DividaResource[],
    credorApi: string,
    dividaId: number
  ): DividaResource[] {
    if (!credorApi || dividaId <= 0) {
      return [];
    }

    const divida = dividasBase.find(d => d.api === credorApi && d.id === dividaId);
    return divida ? [divida] : [];
  }

  /**
   * Seleciona todas as dívidas do credor parceiro (claim Acerto Connect), preservando a ordem
   * relativa entre elas.
   */
  private static selecionarCredorParceiro(
    dividasBase: DividaResource[],
    credorApiParceiro: string
  ): DividaResource[] {
    if (!credorApiParceiro) {
      return [];
    }

    return dividasBase.filter(d => d.api === credorApiParceiro);
  }

  /**
   * Aplica, em ordem, a lista de regras de prioridade sobre `dividasBase`: cada regra devolve o
   * subconjunto (já ordenado entre si) que ela prioriza. A primeira regra que "pegar" uma dívida
   * ganha; o restante mantém a ordem de `dividasBase`. Índice no array = prioridade — inserir uma
   * regra nova é só adicionar um item nessa lista, sem renumerar nada.
   *
   * Ordem: Prime > parceiro (Acerto Connect) > deeplink > estratégia/cache.
   * Prime vem primeiro porque a seção de destaque do Prime só é exibida quando ele é a
   * primeira dívida da listagem; deeplink segue o mesmo encadeamento, só sobe o que
   * ainda não foi priorizado.
   */
  private priorizarDividas(
    dividasBase: DividaResource[],
    credorApiParceiro: string,
    credorApiPrioritario: string,
    dividaIdPrioritario: number
  ): DividaResource[] {
    const regrasPrioridade: Array<() => DividaResource[]> = [
      () =>
        dividaPrimeOrdenacaoService.identificar(
          dividasBase,
          this.veioDoCache.value ? null : this.chavesPrimeiroLote.value
        ),
      () => DividasSyncService.selecionarCredorParceiro(dividasBase, credorApiParceiro),
      () => DividasSyncService.selecionarDeeplink(dividasBase, credorApiPrioritario, dividaIdPrioritario),
    ];

    const chavesPriorizadas = new Set<string>();
    const priorizadas: DividaResource[] = [];

    regrasPrioridade.forEach(regra => {
      regra().forEach(divida => {
        const chave = DividasSyncService.calcularKey(divida.api, divida.id);
        if (chavesPriorizadas.has(chave)) {
          return;
        }
        chavesPriorizadas.add(chave);
        priorizadas.push(divida);
      });
    });

    const resto = dividasBase.filter(
      divida => !chavesPriorizadas.has(DividasSyncService.calcularKey(divida.api, divida.id))
    );

    const lista = [...priorizadas, ...resto];
    dividaPrimeOrdenacaoService.dispararEventoExibicaoSeNecessario(lista);

    return lista;
  }

  private separarDividasPorLote(dividas: DividaResource[]): DividasPorLote {
    return dividas.reduce<DividasPorLote>(
      (lotes, divida) => {
        const chave = DividasSyncService.calcularKey(divida.api, divida.id);
        const lote = this.chavesPrimeiroLote.value.has(chave) ? lotes.dividasPrimeiroLote : lotes.dividasSegundoLote;

        lote.push(divida);
        return lotes;
      },
      {
        dividasPrimeiroLote: [],
        dividasSegundoLote: [],
      }
    );
  }

  private atualizarInfoCredor(
    api: string | undefined,
    infoCredor: SincronizacaoResource['infoCredor'] | undefined
  ): void {
    if (!api || !infoCredor) {
      return;
    }

    this.infoCredor.value = {
      ...this.infoCredor.value,
      [api]: infoCredor,
    };
  }

  constructor() {
    this.viewModel = computed(() => {
      // Força recomputação quando a ordenação final é atualizada (ex.: payload de modelagem)
      const _ = this.ordenacaoVersao.value;

      const deeplinkStore = useDeeplink();
      const credorApiParceiro = sessionService.token?.parceiro ?? '';
      const credorApiPrioritario = deeplinkStore.credorPrioritarioDeeplink ?? '';
      const dividaIdPrioritario = deeplinkStore.dividaIdPrioritarioDeeplink ?? 0;

      const dividasDisponiveisNegociacao = this.todasDividas.value.filter(d => d.deveExibirNaListagemNegociavel);

      const dividasBase = DividasSyncService.obterDividasOrdenadasDoCache(dividasDisponiveisNegociacao);

      const dividasOrdenadas = this.priorizarDividas(
        dividasBase,
        credorApiParceiro,
        credorApiPrioritario,
        dividaIdPrioritario
      );

      const { dividasPrimeiroLote, dividasSegundoLote } = this.separarDividasPorLote(dividasOrdenadas);

      return {
        todasDividas: this.todasDividas.value,
        dividasDisponiveisNegociacao,
        dividasDisponiveisNegociacaoOrdenadas: dividasOrdenadas,
        dividasPrimeiroLote,
        dividasSegundoLote,
        infoCredor: this.infoCredor.value,
        progressoSincronizacao: this.progressoSincronizacao.value,
        isLoading: this.isLoading.value,
        todasChamadasPossuemErro: this.todasChamadasPossuemErro.value,
        thresholdPassou: this.thresholdPassou.value,
        veioDoCache: this.veioDoCache.value,
        exibirTodasDividas: this.exibirTodasDividas.value,
      };
    });
  }

  /**
   * Cria um intervalo que monitora o threshold e processa o primeiro lote automaticamente
   * quando o tempo limite (`getDividasThresholdTempoMs`) for atingido. Retorna função para limpar o intervalo.
   */
  private criarObservacaoThreshold(
    bufferPrimeiroLoteRef: ReturnType<typeof ref<DividaResource[]>>,
    inicioTimestamp: number
  ): () => void {
    let intervaloId: ReturnType<typeof setInterval> | null = null;

    intervaloId = setInterval(() => {
      const tempoDecorrido = Date.now() - inicioTimestamp;
      const buffer = bufferPrimeiroLoteRef.value || [];
      const thresholdPassou = tempoDecorrido >= getDividasThresholdTempoMs();

      if (thresholdPassou) {
        this.thresholdPassou.value = true;
      }

      if (thresholdPassou && this.primeiroLote.length === 0 && buffer.length > 0) {
        this.processarChunkComOrdenacao([...buffer], true);
        // eslint-disable-next-line no-param-reassign
        bufferPrimeiroLoteRef.value = [];
        if (intervaloId) {
          clearInterval(intervaloId);
          intervaloId = null;
        }
      }
    }, 50);

    return () => {
      if (intervaloId) {
        clearInterval(intervaloId);
      }
    };
  }

  /**
   * Aguarda o threshold passar quando a sincronização termina antes do tempo limite.
   * Garante que dívidas que chegaram antes do threshold sejam processadas como primeiro lote.
   */
  private async aguardaThresholdSeNecessario(
    bufferPrimeiroLote: ReturnType<typeof ref<DividaResource[]>>,
    inicioTimestamp: number
  ): Promise<void> {
    if (!bufferPrimeiroLote.value?.length) {
      return;
    }

    const tempoDecorrido = Date.now() - inicioTimestamp;
    const tempoRestante = getDividasThresholdTempoMs() - tempoDecorrido;

    if (tempoRestante > 0) {
      await new Promise<void>(resolve => {
        setTimeout(resolve, tempoRestante);
      });
    }
  }

  /**
   * Verifica e sincroniza API prioritária se existir.
   * Retorna true se encontrou dívidas e deve parar o processo, false caso contrário.
   */
  private async verificarESincronizarApiPrioritaria(): Promise<boolean> {
    const apisPrioritarias = sessionService.token?.apisPrioritarias || [];

    if (apisPrioritarias.length === 0) {
      return false;
    }

    const apiPrioritaria = apisPrioritarias[0];

    try {
      const resultado = await fetchDividasData(apiPrioritaria);
      const dividasPrioritarias = resultado.dividas || [];

      if (dividasPrioritarias.length > 0) {
        this.atualizarInfoCredor(apiPrioritaria, resultado.infoCredor as SincronizacaoResource['infoCredor']);

        // Adiciona as dívidas prioritárias ao estado
        this.todasDividas.value = [...dividasPrioritarias];

        // Processa e ordena as dívidas prioritárias como primeiro lote
        this.processarChunkComOrdenacao(dividasPrioritarias, true);
        this.incrementarProgressoPorCredor(apiPrioritaria);

        // Sincroniza propostas da API prioritária em background
        propostaService.sincronizar(apiPrioritaria).catch(() => {
          // Ignora erros de sincronização de propostas
        });

        // Marca que o threshold passou para finalizar o estado
        this.thresholdPassou.value = true;
        this.isLoading.value = false;

        return true;
      }
    } catch (error) {
      // Em caso de erro na API prioritária, continua com a sincronização normal
      // eslint-disable-next-line no-console
      console.warn('Erro ao sincronizar API prioritária, continuando com sincronização normal:', error);
    }

    return false;
  }

  /**
   * Sincroniza todas as dívidas via streaming, processando incrementalmente conforme chegam.
   * Separa em primeiro lote (até o threshold, ordenado por modelagem Acerto) e segundo lote
   * (restante, por ordem de chegada). Sincroniza propostas automaticamente após concluir.
   */
  public async listarTodasDividas(): Promise<ListarDividasOutput> {
    if (this.isLoading.value) {
      return this.obterOutputAtual();
    }

    this.zerarEstado();
    this.isLoading.value = true;

    const syncAllVeioDoCache = temCacheSyncAllValido();
    this.iniciarCarregamentoOrdenacaoBackend(syncAllVeioDoCache);

    const temApiPrioritariaComDivida = await this.verificarESincronizarApiPrioritaria();
    if (temApiPrioritariaComDivida) {
      return {
        todasDividas: this.viewModel.value.todasDividas,
        dividasDisponiveisNegociacao: this.viewModel.value.dividasDisponiveisNegociacao,
        dividasDisponiveisNegociacaoOrdenadas: this.viewModel.value.dividasDisponiveisNegociacaoOrdenadas,
      };
    }

    const inicioTimestamp = Date.now();
    syncDuracaoDividaTracker.iniciar(inicioTimestamp);
    const bufferPrimeiroLote = ref<DividaResource[]>([]);

    const jaProcessouPrimeiroLote = (): boolean => this.primeiroLote.length > 0;
    const thresholdPassou = (): boolean => {
      const tempoDecorrido = Date.now() - inicioTimestamp;
      return tempoDecorrido >= getDividasThresholdTempoMs();
    };

    const limparIntervalo = this.criarObservacaoThreshold(bufferPrimeiroLote, inicioTimestamp);

    const credoresComFalha = new Set<string>();

    const receberChunks = (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string): void => {
      const novasDividas = chunk.dividas as unknown as DividaResource[];
      const apiChunk = novasDividas[0]?.api ?? api;
      this.atualizarInfoCredor(apiChunk, chunk.infoCredor);
      this.incrementarProgressoPorCredor(apiChunk);
      if (novasDividas.length > 0) {
        this.todasDividas.value = [...this.todasDividas.value, ...novasDividas];
      }

      if (!veioDoCache) {
        syncDuracaoDividaTracker.registrarChegada(novasDividas);
      }

      // Rastreia credores que falharam na sincronização
      if (chunk.sincronizouDividas === false && apiChunk) {
        credoresComFalha.add(apiChunk);
      }

      // Se veio do cache, apenas marca flag e acumula dívidas (processa depois)
      if (veioDoCache) {
        this.veioDoCache.value = true;
        return;
      }

      if (jaProcessouPrimeiroLote()) {
        this.processarChunkComOrdenacao(novasDividas, false);
        return;
      }
      if (thresholdPassou()) {
        if (bufferPrimeiroLote.value.length === 0) {
          this.primeiroLote = [];
          this.chavesPrimeiroLote.value = new Set();
        }
        this.processarChunkComOrdenacao(novasDividas, false);
        return;
      }

      bufferPrimeiroLote.value.push(...novasDividas);
    };

    const sincronizacaoDividasPromise = syncAllDividas(receberChunks);

    try {
      const resultadoSincronizacaoDividas = await sincronizacaoDividasPromise;

      // Quando o sync-all veio do cache, as propostas podem não estar sincronizadas
      // (ex.: F5). No fluxo sem cache, o DividaService já dispara essa sincronização
      // durante a própria sincronização das dívidas.
      if (resultadoSincronizacaoDividas && syncAllVeioDoCache) {
        await this.sincronizarTodasPropostasDoCache(resultadoSincronizacaoDividas);
      }

      // Quando vem do cache, não reordena - a ordenação já está preservada no cache de lotes
      // A ordenação será restaurada pelo viewModel através de obterDividasOrdenadasDoCache
      const todasAsDividasChegaramAntesDoThreshold = !jaProcessouPrimeiroLote() && bufferPrimeiroLote.value.length > 0;

      if (todasAsDividasChegaramAntesDoThreshold) {
        await this.aguardaThresholdSeNecessario(bufferPrimeiroLote, inicioTimestamp);

        // Após aguardar, processa o buffer
        this.processarChunkComOrdenacao([...bufferPrimeiroLote.value], true);
        bufferPrimeiroLote.value = [];
      }

      // Executa fallback para credores que falharam na sincronização
      await this.executarFallbackCredoresComFalha(resultadoSincronizacaoDividas, credoresComFalha, receberChunks);

      const statusSincronizacaoPorApi = resultadoSincronizacaoDividas?.sincronizouDividasPorApi;
      const statusSincronizacoes = statusSincronizacaoPorApi ? Object.values(statusSincronizacaoPorApi) : [];
      this.todasChamadasPossuemErro.value =
        statusSincronizacoes.length > 0 &&
        statusSincronizacoes.every(sincronizou => sincronizou === false) &&
        this.todasDividas.value.length === 0;
    } finally {
      limparIntervalo();
      if (!this.thresholdPassou.value) {
        this.thresholdPassou.value = true;
      }
      this.isLoading.value = false;
    }

    return {
      todasDividas: this.viewModel.value.todasDividas,
      dividasDisponiveisNegociacao: this.viewModel.value.dividasDisponiveisNegociacao,
      dividasDisponiveisNegociacaoOrdenadas: this.viewModel.value.dividasDisponiveisNegociacaoOrdenadas,
    };
  }

  /**
   * Identifica e executa fallback para credores que falharam na sincronização
   */
  private async executarFallbackCredoresComFalha(
    resultadoSincronizacaoDividas:
      | {
          todasDividas: DividaResource[];
          sincronizouDividasPorApi: Record<string, boolean>;
          deveSincronizarPropostasPorApi?: Record<string, boolean>;
          credoresSincronizados?: string[];
        }
      | undefined,
    credoresComFalha: Set<string>,
    receberChunks: (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string) => void
  ): Promise<void> {
    // Identifica credores que falharam na sincronização
    const apisComFalha = new Set<string>();

    // 1. Verifica no resultadoSincronizacaoDividas quais APIs retornaram sincronizouDividas: false
    if (resultadoSincronizacaoDividas?.sincronizouDividasPorApi) {
      Object.entries(resultadoSincronizacaoDividas.sincronizouDividasPorApi).forEach(([api, sincronizou]) => {
        if (sincronizou === false) {
          apisComFalha.add(api);
        }
      });
    }

    // 2. Adiciona credores rastreados durante os chunks que falharam
    credoresComFalha.forEach(api => apisComFalha.add(api));

    // 3. Verifica APIs que não apareceram no resultado (não sincronizaram)
    // Compara com todas as APIs disponíveis no token
    const todasApis = sessionService.token?.apis || [];
    const apisSincronizadas = new Set([
      ...(resultadoSincronizacaoDividas?.credoresSincronizados || []),
      ...Object.keys(resultadoSincronizacaoDividas?.sincronizouDividasPorApi || {}),
    ]);

    todasApis.forEach(api => {
      // Se a API não foi sincronizada e não é whitelabel, tenta sincronizar individualmente
      if (!apisSincronizadas.has(api) && !ConvivenciaWlSevice.isWhiteLabel(api)) {
        apisComFalha.add(api);
      }
    });

    // Tenta sincronizar individualmente os credores que falharam
    if (apisComFalha.size > 0) {
      await this.tentarSincronizarCredoresIndividuais(Array.from(apisComFalha), receberChunks);
    }
  }

  /**
   * Tenta sincronizar individualmente credores que falharam na sincronização inicial
   */
  private async tentarSincronizarCredoresIndividuais(
    apisComFalha: string[],
    receberChunks: (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string) => void
  ): Promise<void> {
    // Sincroniza cada credor individualmente em paralelo
    const resultados = await Promise.allSettled(
      apisComFalha.map(async api => {
        try {
          const resultado = await fetchDividasData(api);
          return { api, resultado };
        } catch (_error) {
          // Ignora erros individuais para não quebrar o fluxo
          return { api, resultado: undefined };
        }
      })
    );

    // Processa os resultados da sincronização individual
    resultados.forEach(resultado => {
      if (resultado.status === 'fulfilled' && resultado.value.resultado) {
        const { api, resultado: processedData } = resultado.value;
        const novasDividas = processedData.dividas;
        this.atualizarInfoCredor(api, processedData.infoCredor as SincronizacaoResource['infoCredor']);

        propostaService.sincronizar(api).catch(() => {
          // Ignora erros de sincronização de propostas
        });

        if (novasDividas.length > 0) {
          // Atualiza o estado com as novas dívidas
          this.todasDividas.value = [...this.todasDividas.value, ...novasDividas];

          const infoCredor = processedData.infoCredor as {
            periodoIndisponibilidade?: {
              dataInicio: string;
              dataFim: string;
              motivo: string;
            };
          } | null;

          const sincronizacaoResource = new SincronizacaoResource({
            dividas: novasDividas.map(d => d as unknown as DividaDto),
            sincronizouDividas: true,
            periodoIndisponibilidade: infoCredor?.periodoIndisponibilidade,
          });

          this.processarChunkComOrdenacao(novasDividas, false);

          // Chama o callback para notificar sobre as novas dívidas
          receberChunks(sincronizacaoResource, false, api);
        }
      }
    });
  }

  /**
   * Sincroniza propostas para todos os credores quando os dados vieram do cache
   * Dois cenários:
   * 1. Whitelabel: sincroniza TODAS as APIs Whitelabel que sincronizaram (mesmo sem dívidas)
   * 2. MC (Multicredor): sincroniza APIs indicadas em deveSincronizarPropostasPorApi
   */
  private async sincronizarTodasPropostasDoCache(cacheData: {
    todasDividas: DividaResource[];
    sincronizouDividasPorApi: Record<string, boolean>;
    deveSincronizarPropostasPorApi?: Record<string, boolean>;
    credoresSincronizados?: string[];
  }): Promise<void> {
    const apisParaSincronizar = new Set<string>();

    // Cenário 1: Whitelabel - adiciona TODAS as APIs Whitelabel que sincronizaram
    Object.entries(cacheData.sincronizouDividasPorApi).forEach(([api, sincronizou]) => {
      if (sincronizou === true && ConvivenciaWlSevice.isWhiteLabel(api)) {
        apisParaSincronizar.add(api);
      }
    });

    // Cenário 2: MC - adiciona APIs que devem sincronizar propostas (mesmo sem dívidas)
    if (cacheData.deveSincronizarPropostasPorApi) {
      Object.entries(cacheData.deveSincronizarPropostasPorApi).forEach(([api, deveSincronizar]) => {
        if (deveSincronizar === true) {
          apisParaSincronizar.add(api);
        } else {
          propostaService.registrarPropostasAtualizadas(api);
        }
      });
    }

    // Sincroniza propostas em background para todas as APIs
    apisParaSincronizar.forEach(api => {
      propostaService.sincronizar(api).catch(() => {
        // Ignora erros de sincronização de propostas
      });
    });
  }

  private iniciarCarregamentoOrdenacaoBackend(syncAllVeioDoCache: boolean): void {
    if (syncAllVeioDoCache) {
      return;
    }

    ordenacaoBackendService
      .buscarOrdenacao()
      .then(data => {
        if (data) {
          this.ordenacaoRecomendadaPorModelo = data;
          OrdenacaoPorModelagemAcertoStrategy.atualizarOrdenacaoRecomendada(data);
        }
        this.ordenacaoVersao.value += 1;
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn('DividasSyncService: falha ao buscar ordenação no backend', err);
      });
  }

  /**
   * Processa e ordena um chunk de dívidas baseado no momento de chegada.
   * Primeiro lote (até threshold): ordena por MODELAGEM_ACERTO.
   * Segundo lote (após threshold): ordena por ORDEM_CHEGADA.
   * @param chunkDividas - Dívidas a processar
   * @param isPrimeiroLote - Se true, processa como primeiro lote; se false, como segundo lote
   */
  private processarChunkComOrdenacao(chunkDividas: DividaResource[], isPrimeiroLote: boolean): void {
    if (chunkDividas.length === 0) {
      return;
    }

    const orderingService = getDividasOrderingService();
    const dividasNegociaveis = chunkDividas.filter(d => d.podeSerNegociada);

    if (dividasNegociaveis.length === 0) {
      return;
    }

    if (isPrimeiroLote && this.primeiroLote.length === 0) {
      const loteOrdenado = orderingService.ordenarLotes([dividasNegociaveis], [TipoOrdenacao.MODELAGEM_ACERTO]);
      this.primeiroLote = loteOrdenado;
      this.chavesPrimeiroLote.value = new Set(loteOrdenado.map(d => DividasSyncService.calcularKey(d.api, d.id)));
      orderingService.salvarLotesOrdenacao(
        [loteOrdenado, []],
        [TipoOrdenacao.MODELAGEM_ACERTO, TipoOrdenacao.ORDEM_CHEGADA]
      );
      return;
    }

    const todasDividasNegociaveis = this.todasDividas.value.filter(d => d.podeSerNegociada);

    if (!isPrimeiroLote && this.primeiroLote.length === 0) {
      const loteOrdenado = orderingService.ordenarLotes([todasDividasNegociaveis], [TipoOrdenacao.ORDEM_CHEGADA]);

      orderingService.salvarLotesOrdenacao(
        [[], loteOrdenado],
        [TipoOrdenacao.MODELAGEM_ACERTO, TipoOrdenacao.ORDEM_CHEGADA]
      );
      return;
    }

    const dividasSegundoLote = todasDividasNegociaveis.filter(
      d => !this.chavesPrimeiroLote.value.has(DividasSyncService.calcularKey(d.api, d.id))
    );

    const segundoLoteOrdenado = orderingService.ordenarLotes([dividasSegundoLote], [TipoOrdenacao.ORDEM_CHEGADA]);

    orderingService.salvarLotesOrdenacao(
      [this.primeiroLote, segundoLoteOrdenado],
      [TipoOrdenacao.MODELAGEM_ACERTO, TipoOrdenacao.ORDEM_CHEGADA]
    );
  }

  /**
   * Busca uma dívida específica pelo identificador único (api + id)
   */
  public getDividaById(api: string | undefined, id: number): DividaResource | undefined {
    if (!api || !id) return undefined;
    const key = DividasSyncService.calcularKey(api, id);
    return this.todasDividas.value.find(d => DividasSyncService.calcularKey(d.api, d.id) === key);
  }

  /**
   * Atualiza uma dívida existente ou adiciona uma nova ao estado interno
   */
  public updateOrCreateDivida(api: string, divida: DividaResource): void {
    const key = DividasSyncService.calcularKey(api, divida.id);
    const index = this.todasDividas.value.findIndex(d => DividasSyncService.calcularKey(d.api, d.id) === key);

    if (index >= 0) {
      this.todasDividas.value[index] = divida;
    } else {
      this.todasDividas.value.push(divida);
    }
  }

  /**
   * Busca uma dívida específica pelo identificador único (api + id)
   */
  public obterDivida(api: string, id: number): Promise<DividaResource> {
    return dividaService.obterDivida(api, id);
  }

  public removeDividaNegociavel(api: string, id: number): void {
    const index = this.todasDividas.value.findIndex(
      d => DividasSyncService.calcularKey(d.api, d.id) === DividasSyncService.calcularKey(api, id)
    );
    if (index >= 0) {
      this.todasDividas.value.splice(index, 1);
    }
  }

  /**
   * Recupera dívidas ordenadas do cache de lotes, se disponível.
   * Retorna as dívidas originais se o cache não estiver disponível ou incompleto.
   */
  private static obterDividasOrdenadasDoCache(dividas: DividaResource[]): DividaResource[] {
    if (dividas.length === 0) {
      return dividas;
    }

    const orderingService = getDividasOrderingService();
    const dividasOrdenadas = orderingService.ordenarUsandoLotesCache(dividas);

    if (!dividasOrdenadas || dividasOrdenadas.length < dividas.length) {
      return dividas;
    }

    return dividasOrdenadas;
  }
}

const dividasSyncService = new DividasSyncService();
export default dividasSyncService;
